import { useDyteMeeting } from '@dytesdk/react-web-core';
import { DyteParticipant } from '@dytesdk/web-core';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isUserAdmin, sendMessageToParent } from 'shared-utils';
import { useSessionContext } from 'src/context/SessionContextProvider';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { SessionJoineeStatus, SessionType, UISession, UserRole } from 'types';
import InterviewNavbar from '../interviews/InterviewNavbar';
import WaitingForHost from '../interviews/WaitingForHost';
import PostJoinedSessionScreen from './PostJoinedSessionScreen';
import JoinConfirmation from '../interviews/JoinConfirmation';
import SessionFooter from './SessionFooter';
import SessionEndModal from '../shared/SessionEndModal';
import { createSessionLog, endSession } from 'src/apis/session';
import { get } from 'lodash';
import { endMeeting } from 'src/apis/meeting';
import { ONWARD_DASHBOARD_BASE_URL } from 'src/config/env.config';
import { useGetSessionByIdQuery } from 'src/hooks/session.hooks';

type Props = {
	isHost: boolean;
};

const LiveSessionScreen = ({ isHost }: Props) => {
	// eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI2NjBlOGJkODNlMDIyOTM2MDQ3ODQ5MGQiLCJzbHVnIjoidGVzdFNsdWcxIiwiaWF0IjoxNzEyMzg3OTUwLCJleHAiOjE3MTQ5Nzk5NTB9.JQwXGZNzVgrfxTfIVmRLGRSPhas58HO5x8xtdgWibYQ
	const toast = useToast();
	const [showEndSessionModal, setShowEndSessionModal] = useState(false);
	const { user: userData, isAlumniConnectLoggedIn, isMentorLoggedIn, isJobseekerLoggedIn } = useUserContext();
	const dyteMeeting = useDyteMeeting();
	const navigate = useNavigate();

	const { sessionId } = useParams();
	console.log('sessionId', sessionId);
	const queryClient = useQueryClient();
	const [joined, setJoined] = useState(false);
	const [hostJoined, setHostJoined] = useState(isHost);
	const [screenShareParticipant, setScreenShareParticipant] = useState<DyteParticipant>();
	const [participant, setParticipant] = useState<DyteParticipant>();
	// const [allParticipants, setAllParticipants] = useState<DyteParticipant[]>([]);

	const [studentParticipant, setStudentParticipant] = useState<DyteParticipant>();
	const [hostParticipant, setHostParticipant] = useState<DyteParticipant>();

	const [rescheduleReason, setRescheduleReason] = useState<string>('');
	const [session, setSession] = useState<UISession>();
	const sessionResponse = useGetSessionByIdQuery(sessionId, '');
	useEffect(() => {
		console.log('sessionResponse:', sessionId, session, sessionResponse);
		setSession(sessionResponse?.data?.session);
	}, [sessionResponse]);

	useEffect(() => {
		console.log('session updated:', session);
	}, [session]);

	useEffect(() => {
		console.log('hostJoined----:', hostJoined);
	}, [hostJoined]);

	const { setRefetchUserDetails } = useSessionContext();
	const endSessionFn = useMutation({
		mutationKey: 'endMeeting',
		mutationFn: endSession,
		onSuccess(data) {
			queryClient.refetchQueries(['getAllSessions']);
			queryClient.refetchQueries(['verifyCookie']);

			if (isAlumniConnectLoggedIn) {
				navigate(`/alumni/sessions`);
			} else if (isMentorLoggedIn) {
				navigate(`/mentors/sessions`);
			}
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			queryClient.refetchQueries(['getRounds']);
			queryClient.refetchQueries(['getMeetings']);
			queryClient.refetchQueries(['verifyCookie']);
		},
	});
	const [isRescheduleRecommended, setIsRescheduleRecommended] = useState<boolean>(true);
	useEffect(() => {
		let participants = dyteMeeting.meeting.participants.joined.toArray();
		if (participants.length > 0) {
			setParticipant(participants[0]);
			setHostJoined(isHost || true);
		}
		dyteMeeting.meeting.self.on('roomJoined', () => {
			setJoined(true);
		});
		dyteMeeting.meeting.self.addListener('roomLeft', ({ state }) => {
			console.log('room left----:', state);
			queryClient.refetchQueries(['getSessions']);
			queryClient.refetchQueries(['verifyCookie']);
			setRefetchUserDetails(true);
			console.log('state:----', state);
			if (state === 'ended') {
				console.log('3. sending message to parent', sessionId, isMentorLoggedIn);
				setShowEndSessionModal(true);
				if (isAlumniConnectLoggedIn || isMentorLoggedIn) {
					endSessionFn.mutate(sessionId ?? '');
				} else if (isJobseekerLoggedIn) {
					// navigate('/dashboard');
					navigate('/my-applications');
				} else {
					console.log('1. sending message to parent');
					sendMessageToParent();
				}
			} else if (state === 'left') {
				console.log('leaving session...');
				if (isUserAdmin(userData?.role as UserRole)) navigate('/admin');
				else if (isAlumniConnectLoggedIn) navigate('/alumni/sessions');
				else if (isMentorLoggedIn) navigate('/mentors/sessions');
				else if (isJobseekerLoggedIn) {
					// navigate('/dashboard');
					navigate('/my-applications');
				} else {
					console.log('2. sending message to parent');
					sendMessageToParent();
				}
				leaveSession(userData?._id || session?.participant?._id);
			}
		});

		dyteMeeting.meeting.participants.joined.addListener('participantJoined', participant => {
			console.log('participant joined:', participant);
			setParticipant(participant);
			if (participant.presetName === 'meeting_participant') {
				setStudentParticipant(participant);
			} else if (participant.presetName === 'meeting_host') {
				setHostParticipant(participant);
			}
			setHostJoined(isHost || true);
		});

		dyteMeeting.meeting.participants.joined.addListener('audioUpdate', participant => {
			console.log('audio update:', participant);
			setParticipant(participant);
			if (participant.presetName === 'meeting_participant') {
				setStudentParticipant(participant);
			} else if (participant.presetName === 'meeting_host') {
				setHostParticipant(participant);
			}
			// setAllParticipants(dyteMeeting.meeting.participants.joined.toArray());
		});

		dyteMeeting.meeting.participants.joined.addListener('participantLeft', participant => {
			setParticipant(undefined);
			if (participant.presetName === 'meeting_participant') {
				setStudentParticipant(undefined);
			} else if (participant.presetName === 'meeting_host') {
				setHostParticipant(undefined);
			}
			// setAllParticipants(dyteMeeting.meeting.participants.joined.toArray());
			setHostJoined(isHost || false);
		});

		dyteMeeting.meeting.participants.joined.addListener('screenShareUpdate', participant => {
			if (participant.screenShareEnabled) setScreenShareParticipant(participant);
			else setScreenShareParticipant(undefined);
		});

		return () => {
			dyteMeeting.meeting.participants.joined.removeAllListeners();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	console.log('hostJoined', hostJoined, isHost);
	const addUserSessionLog = useMutation({
		mutationKey: 'createSessionLog',
		mutationFn: createSessionLog,
	});
	const leaveSession = (id: string) => {
		console.log('2. leaving session...', sessionResponse.data?.session);
		const session = sessionResponse.data?.session;
		if (session && (session?.levelUpParticipant?._id || session?.participant?._id)) {
			const payload = {
				participant:
					session.type === SessionType.MENTOR_SESSION ? session?.levelUpParticipant?._id : session?.participant?._id,
				status: SessionJoineeStatus.LEFT,
				sessionId: session.dyteMeetingId,
			};
			addUserSessionLog.mutate(payload);
			if (session.type === SessionType.MENTOR_SESSION && isJobseekerLoggedIn) {
				navigate('/allMentors');
			}
		}
	};

	return (
		<>
			<InterviewNavbar showNavbar={isHost} joined={joined} />
			<div className="px-5 py-4 h-full w-full flex flex-col">
				{joined ? (
					hostJoined || hostParticipant ? (
						<PostJoinedSessionScreen
							// allParticipants={allParticipants}
							hostParticipant={hostParticipant}
							studentParticipant={studentParticipant}
							participant={participant}
							screenShareParticipant={screenShareParticipant}
						/>
					) : (
						<WaitingForHost />
					)
				) : (
					<JoinConfirmation />
				)}
				{joined ? (
					<SessionFooter
						isHost={isHost}
						showEndSessionModal={showEndSessionModal}
						setShowEndSessionModal={setShowEndSessionModal}
					/>
				) : (
					<div />
				)}
			</div>
			{/* <SessionEndModal
				isOpen={showEndSessionModal}
				isSuccessfull={isRescheduleRecommended}
				reason={rescheduleReason}
				setIsSuccessfull={setIsRescheduleRecommended}
				setReason={setRescheduleReason}
				setShowEndSessionModal={setShowEndSessionModal}
				handleSubmit={() => {
					endSessionFn.mutate(sessionId ?? '');
					setShowEndSessionModal(false);
				}}
				handleLeave={() => {
					dyteMeeting.meeting.leave();
					setShowEndSessionModal(false);
				}}
			/> */}
		</>
	);
};

export default LiveSessionScreen;
